import { fetchStudyPatientInfo } from '@/entities/study';
import { convertDecimalHeightToFeetInch } from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import _ from 'lodash';
import { useEffect } from 'react';
import type React from 'react';
import { checkIcorDevice } from '@/shared/ui';
import type { PatientInfoDrawerProps } from './patientInfoDrawer';

type UsePatientInfoDrawerArgs = Pick<
  PatientInfoDrawerProps,
  'studyId' | 'visible' | 'handleClickCloseButton'
>;

const usePatientInfoDrawer = ({
  studyId,
  visible,
  handleClickCloseButton,
}: UsePatientInfoDrawerArgs) => {
  const [state, setState] = useMergeState<{ isScroll: boolean; data: any[] }>({
    isScroll: false,
    data: [],
  });

  /**
   * Lays out the patient info drawer sections from a study's onboarding and demographic record. Builds Patient Information with the height converted to feet and inches and the weight fixed to one decimal, Patient Contact, a Secondary Contact section only when one of its four fields is present, and Diagnostic Information, which appends the secondary diagnosis code when set and an AFib Detected row only for non-iCor devices.
   *
   * @param study - Study record supplying patient info, onboarding answers and the recording device.
   * @returns An array of drawer section descriptors, one entry left undefined when there is no secondary contact.
   */
  const generatePatientInfoData = (study: any) => {
    const { info, onboarding, device } = study || {};
    const heightFormat = convertDecimalHeightToFeetInch(info?.patient?.height);

    const list: any[] = [
      {
        header: 'Patient Information',
        content: [
          {
            label: 'Name',
            value: `${info?.patient?.firstName} ${info?.patient?.lastName}` || '',
          },
          {
            label: 'Gender',
            value: info?.patient?.gender || '',
          },
          {
            label: 'ID',
            value: info?.patient?.patientId || '',
          },
          {
            label: 'DOB',
            value: info?.patient?.dob || '',
          },
          {
            label: 'Height',
            value: !_.isEmpty(heightFormat) ? `${heightFormat.ft} ft ${heightFormat.ins} in` : '--',
          },
          {
            label: 'Weight',
            value: info?.patient?.weight
              ? `${parseFloat(info?.patient.weight).toFixed(1)} lb`
              : '--',
          },
          {
            label: 'Pacemaker',
            value: onboarding?.pacer || '',
          },
        ],
      },
      {
        header: 'Patient Contact',
        content: [
          {
            label: 'Address',
            value: `${info?.patient?.contact?.address}, ${info?.patient?.contact?.city}, ${info?.patient?.contact?.state}`,
          },
          {
            label: 'Zip code',
            value: info?.patient?.contact?.zip || '',
          },
          {
            label: 'Phone Number',
            value: info?.patient?.contact?.phone1 || '',
          },
          {
            label: '2nd Phone Number',
            value: info?.patient?.contact?.phone2 || '',
          },
          {
            label: 'Email',
            value: info?.patient?.email || '',
          },
        ],
      },
      info?.patient?.secondaryContact?.name ||
      info?.patient?.secondaryContact?.phone ||
      info?.patient?.secondaryContact?.relationship ||
      info?.patient?.secondaryContact?.email
        ? {
            header: 'Secondary Contact',
            content: [
              {
                label: 'Name',
                value: info?.patient?.secondaryContact?.name || '',
              },
              {
                label: 'Phone Number',
                value: info?.patient?.secondaryContact?.phone || '',
              },
              {
                label: 'Relationship',
                value: info?.patient?.secondaryContact?.relationship || '',
              },
              {
                label: 'Email',
                value: info?.patient?.secondaryContact?.email || '',
              },
            ],
          }
        : undefined,
    ];

    const data = {
      header: 'Diagnostic Information',
      content: [
        {
          label: 'Medications',
          value: info?.diagnosis?.patientMedications || '',
        },
        {
          label: 'Insurance',
          value: info?.insurance?.type || '',
        },
        {
          label: 'Primary diagnosis code',
          value: info?.diagnosis?.primary ? info.diagnosis.primary : '',
        },
      ],
    };

    if (info?.diagnosis?.second) {
      data.content.push({
        label: 'Secondary diagnosis code',
        value: info?.diagnosis?.second ? info.diagnosis.second : '',
      });
    }

    const isIcorDevice = checkIcorDevice(device);
    if (!isIcorDevice) {
      (data.content as any[]).push({
        label: 'AFib Detected',
        value: (study?.hasAFibCode ? true : study?.isAFibDetected) ? 'Activated' : 'Disabled',
      });
    }

    list.push(data);

    return list;
  };

  /**
   * Loads the patient behind the current study into the drawer. Fetches the study's patient info and lays it out into drawer sections, storing them in state; a Study not found error toasts Study was deleted and closes the drawer, while any other error is logged and toasted.
   *
   * @returns A promise resolving once the patient sections are in state.
   */
  const fetchData = async () => {
    try {
      const result = await fetchStudyPatientInfo(studyId);
      const data = generatePatientInfoData((result as any).data.study);
      setState({ data });
    } catch (error: any) {
      console.error('Failed to fetch study patient info: ', error);

      if (error.message === 'Error: Study not found') {
        toastrError('Study was deleted', 'Error');
        handleClickCloseButton();
        return;
      }

      toastrError(error.message, 'Error');
    }
  };

  /**
   * Tracks whether the patient info drawer body has been scrolled away from the top so the header can render its shadow. Reads scrollTop off the scrolled div and flips the isScroll flag in local state only when the top or not-top condition disagrees with the flag already stored.
   *
   * @param e - Scroll event from the drawer's scrollable container.
   * @returns Nothing.
   */
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (e?.target) {
      const { scrollTop } = e.target as HTMLDivElement;
      if (scrollTop === 0 && state.isScroll) {
        setState({ isScroll: false });
      } else if (scrollTop !== 0 && !state.isScroll) {
        setState({ isScroll: true });
      }
    }
  };

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [studyId, visible]);

  useUpdateEffect(() => {
    if (visible) {
      const myDiv = document.getElementById('drawer-body-container');
      if (myDiv) {
        myDiv.scrollTo({ top: 0 });
      }
    }
  }, [visible]);

  return { state, handleScroll };
};

export default usePatientInfoDrawer;
